import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, BarChart2, Monitor, Users, Package, Shield, Menu, Moon, Sun, CheckCircle, Palette } from 'lucide-react';
import UserProfileDropdown from './UserProfileDropdown';
import NotificationsDropdown from './NotificationsDropdown';
import axios from 'axios';

// Importar secciones
import ServicesSection from './sections/ServicesSection';
import ClientsSection from './sections/ClientsSection';
import AccountsSection from './sections/AccountsSection';
import MovementsSection from './sections/MovementsSection';
import UsersSection from './sections/UsersSection';

// Importar hooks para obtener los datos necesarios para las secciones
import { useServices } from './hooks/useServices';
import { useClients } from './hooks/useClients';
import { useAccounts } from './hooks/useAccounts';
import { useMovements } from './hooks/useMovements';

// Tema predeterminado y temas disponibles
const themes = {
  dark: {
    name: 'Oscuro',
    bgPrimary: 'bg-gray-900',
    bgSecondary: 'bg-gray-800',
    bgTertiary: 'bg-gray-700',
    borderColor: 'border-gray-900',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    accent: 'purple'
  },
  midnight: {
    name: 'Medianoche',
    bgPrimary: 'bg-slate-900',
    bgSecondary: 'bg-slate-800',
    bgTertiary: 'bg-slate-700',
    borderColor: 'border-slate-900',
    textPrimary: 'text-white',
    textSecondary: 'text-slate-300',
    textMuted: 'text-slate-400',
    accent: 'indigo'
  },
  dracula: {
    name: 'Drácula',
    bgPrimary: 'bg-[#282a36]',
    bgSecondary: 'bg-[#44475a]',
    bgTertiary: 'bg-[#6272a4]',
    borderColor: 'border-[#44475a]',
    textPrimary: 'text-[#f8f8f2]',
    textSecondary: 'text-[#f8f8f2]',
    textMuted: 'text-[#bd93f9]',
    accent: 'purple'
  }
};

const Dashboard = () => {
  const [activeSection, setActiveSection] = useState('cuentas');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('dark');
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, csrfToken } = useAuth();
  const themeSelectorRef = useRef(null);

  // Obtener datos de los hooks
  const { services, fetchServices } = useServices();
  const { clients, fetchClients } = useClients();
  const { accounts, fetchAccounts } = useAccounts();
  const { movements, fetchMovements } = useMovements();

  // Función para alternar el colapso del sidebar
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Función para cambiar el tema
  const changeTheme = (theme) => {
    setCurrentTheme(theme);
    localStorage.setItem('netfly-theme', theme);
    setShowThemeSelector(false);
  };

  // Cargar tema guardado al iniciar
  useEffect(() => {
    const savedTheme = localStorage.getItem('netfly-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Cerrar selector de tema al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (themeSelectorRef.current && !themeSelectorRef.current.contains(event.target)) {
        setShowThemeSelector(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Configurar axios para usar CSRF token
  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    }
  }, [csrfToken]);

  // Detectar si estamos en la ruta de administración de usuarios
  useEffect(() => {
    if (location.pathname.includes('/admin/users')) {
      setActiveSection('usuarios');
    }
  }, [location]);

  // Exponer los servicios a nivel global para el componente de WhatsApp
  useEffect(() => {
    if (services.length > 0) {
      window.services = services;
    }
  }, [services]);

  // Cargar datos iniciales
  useEffect(() => {
    // Cargar datos solo una vez al montar el componente
    const loadInitialData = async () => {
      await Promise.all([
        fetchServices(),
        fetchClients(),
        fetchAccounts(),
        fetchMovements()
      ]);
    };
    
    loadInitialData();
  }, []); // Dependencia vacía = ejecutar solo al montar

  // Función para cambiar sección activa
  const handleSectionChange = (section) => {
    if (section === 'usuarios' && currentUser?.role === 'admin') {
      navigate('/admin/users');
    } else {
      setActiveSection(section);
    }
  };

  // Renderizar la sección activa
  const renderActiveSection = () => {
    switch (activeSection) {
      case 'usuarios':
        return <UsersSection />;
      case 'clientes':
        return (
          <ClientsSection 
            themes={themes} 
            currentTheme={currentTheme} 
            accounts={accounts} 
            services={services}
          />
        );
      case 'movimientos':
        return (
          <MovementsSection 
            themes={themes} 
            currentTheme={currentTheme} 
            services={services} 
            clients={clients}
          />
        );
      case 'cuentas':
        return (
          <AccountsSection 
            themes={themes} 
            currentTheme={currentTheme} 
            services={services} 
            clients={clients}
          />
        );
      case 'servicios':
        return (
          <ServicesSection 
            themes={themes} 
            currentTheme={currentTheme} 
            accounts={accounts}
          />
        );
      default:
        return <div className="p-6">Selecciona una sección</div>;
    }
  };

  return (
    <div className={`flex h-screen ${themes[currentTheme].bgPrimary} ${themes[currentTheme].textPrimary}`}>
      {/* Sidebar */}
      <div className={`${themes[currentTheme].bgSecondary} border-r ${themes[currentTheme].borderColor} transition-all duration-300 ${sidebarCollapsed ? 'w-24' : 'w-56'} flex flex-col`}>
        <div className={`px-4 py-3 border-b ${themes[currentTheme].borderColor} flex items-center justify-between`}>
          {!sidebarCollapsed && <div className="text-xl font-bold text-purple-400">𝕋𝕙𝕖𝕟𝕕 𝕆𝕟𝕝𝕚𝕟𝕖</div>}
          <button 
            className={`${themes[currentTheme].textMuted} hover:${themes[currentTheme].textPrimary} ${sidebarCollapsed ? 'p-2' : 'p-1'}`}
            onClick={toggleSidebar}
          >
            <Menu size={sidebarCollapsed ? 26 : 20} />
          </button>
        </div>
        
        {/* Espacio de separación */}
        <div className="py-4"></div>
        
        {/* Contenedor de navegación con bordes y espaciado */}
        <nav className="mx-2 p-2 rounded-lg bg-gray-900 flex flex-col gap-1">
          <button 
            className={`flex items-center ${sidebarCollapsed ? 'px-3 py-4 justify-center' : 'px-3 py-2'} w-full rounded-md ${activeSection === 'cuentas' ? themes[currentTheme].bgTertiary : `hover:${themes[currentTheme].bgTertiary}`}`}
            onClick={() => handleSectionChange('cuentas')}
          >
            <Monitor className={themes[currentTheme].textMuted} size={sidebarCollapsed ? 22 : 20} />
            {!sidebarCollapsed && <span className={`text-sm ml-3 ${themes[currentTheme].textSecondary}`}>Cuentas</span>}
          </button>

          <button 
            className={`flex items-center ${sidebarCollapsed ? 'px-3 py-4 justify-center' : 'px-3 py-2'} w-full rounded-md ${activeSection === 'clientes' ? themes[currentTheme].bgTertiary : `hover:${themes[currentTheme].bgTertiary}`}`}
            onClick={() => handleSectionChange('clientes')}
          >
            <Users className={themes[currentTheme].textMuted} size={sidebarCollapsed ? 22 : 20} />
            {!sidebarCollapsed && <span className={`text-sm ml-3 ${themes[currentTheme].textSecondary}`}>Clientes</span>}
          </button>

          <button 
            className={`flex items-center ${sidebarCollapsed ? 'px-3 py-4 justify-center' : 'px-3 py-2'} w-full rounded-md ${activeSection === 'servicios' ? themes[currentTheme].bgTertiary : `hover:${themes[currentTheme].bgTertiary}`}`}
            onClick={() => handleSectionChange('servicios')}
          >
            <Package className={themes[currentTheme].textMuted} size={sidebarCollapsed ? 22 : 20} />
            {!sidebarCollapsed && <span className={`text-sm ml-3 ${themes[currentTheme].textSecondary}`}>Servicios</span>}
          </button>

          {/* Sección de Gestión de Usuarios (solo para administradores) */}
          {currentUser?.role === 'admin' && (
            <button 
              className={`flex items-center ${sidebarCollapsed ? 'px-3 py-4 justify-center' : 'px-3 py-2'} w-full rounded-md ${activeSection === 'usuarios' ? themes[currentTheme].bgTertiary : `hover:${themes[currentTheme].bgTertiary}`}`}
              onClick={() => handleSectionChange('usuarios')}
            >
              <Shield className={themes[currentTheme].textMuted} size={sidebarCollapsed ? 22 : 20} />
              {!sidebarCollapsed && <span className={`text-sm ml-3 ${themes[currentTheme].textSecondary}`}>Usuarios</span>}
            </button>
          )}
        </nav>
        
        {/* Espacio flexible para empujar el resto hacia abajo */}
        <div className="flex-grow"></div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navbar */}
        <header className={`${themes[currentTheme].bgSecondary} border-b ${themes[currentTheme].borderColor}`}>
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              {/* Dejamos este espacio vacío ya que el buscador se movió a cada sección */}
            </div>
            <div className="flex items-center space-x-3">
              <NotificationsDropdown />
              
              {/* Selector de tema */}
              <div ref={themeSelectorRef} className="relative">
                <button 
                  className={`rounded-full ${themes[currentTheme].bgTertiary} p-1`}
                  onClick={() => setShowThemeSelector(!showThemeSelector)}
                >
                  {currentTheme === 'dark' ? 
                    <Moon size={20} className={themes[currentTheme].textMuted} /> : 
                    <Sun size={20} className={themes[currentTheme].textMuted} />
                  }
                </button>
                
                {showThemeSelector && (
                  <div className={`absolute right-0 mt-2 w-48 ${themes[currentTheme].bgSecondary} rounded-md shadow-lg z-10 border ${themes[currentTheme].borderColor}`}>
                    <div className="p-2">
                      <h3 className={`text-sm font-bold ${themes[currentTheme].textPrimary} mb-1 px-2 py-1 flex items-center`}>
                        <Palette size={16} className="mr-2" /> Temas
                      </h3>
                      <div className="space-y-1">
                        {Object.keys(themes).map(theme => (
                          <button 
                            key={theme}
                            className={`w-full text-left px-2 py-1 text-sm rounded flex items-center ${
                              currentTheme === theme ? themes[theme].bgTertiary : ''
                            }`}
                            onClick={() => changeTheme(theme)}
                          >
                            {theme === 'dark' ? <Moon size={16} className="mr-2" /> : 
                             theme === 'dracula' ? <Moon size={16} className="mr-2 text-purple-400" /> :
                             <Sun size={16} className="mr-2" />}
                            <span className={themes[currentTheme].textPrimary}>{themes[theme].name}</span>
                            {currentTheme === theme && (
                              <CheckCircle size={16} className="ml-auto text-green-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Componente de perfil de usuario con dropdown */}
              <UserProfileDropdown />
            </div>
          </div>
        </header>
        
        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          {renderActiveSection()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
